import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  SecurePaper, useKeyGuard, useFocusLoss, pickAnswer, isAnswered,
  QuestionStep, ReviewStep, ConfirmSubmitModal, SittingRules, QuizFacts, QuizResultCard,
} from './QuizSitting';

/**
 * Preview — the paper exactly as a participant gets it: the same page
 * (components/lnd/QuizSitting.js, shared with pages/QuizPage.js), the same
 * intro and rules, the same one-question-at-a-time flow and shuffle, the
 * same watermark and copy guards, the same grading and the same result card
 * — no answer key, just as a participant gets none. One thing only the
 * preview shows: the Focus lost count (counted exactly as in a real sitting,
 * where the participant never sees it).
 * Entirely client-side: nothing is saved, no attempt row exists, no time is
 * measured. Opened from the Quiz Bank list, the Quiz Bank editor and the
 * trainer's quiz editor on the session page (the editors preview the
 * questions AS CURRENTLY EDITED, saved or not).
 *
 * Props: { title, description, passMark, questions, onClose }
 * `questions`: [{ type, text, points, mustPass, options: [{ key, text, correct }] }]
 * — use normalizePreviewQuestions() to build it from either the editor's
 * form state or the API's { options, correct } shape.
 */

const OPTION_KEYS = 'abcdefghij'.split('');

/** Editor rows ({options:[{text,correct}]}) or API rows ({options:[{key,text}], correct:[key]}) → one shape. */
export const normalizePreviewQuestions = (list) => (list || []).map((q) => ({
  type: q.type,
  text: q.text,
  points: Number(q.points) || 1,
  mustPass: q.mustPass === true,
  options: (q.options || []).map((o, i) => ({
    key: o.key || OPTION_KEYS[i],
    text: o.text,
    correct: o.correct === true || (Array.isArray(q.correct) && q.correct.includes(o.key)),
  })),
}));

const shuffle = (list) => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const sameSet = (a, b) => a.length === b.length && b.every((k) => a.includes(k));

// The same per-person shuffle a real sitting gets — questions and options.
const shufflePaper = (raw) => shuffle(raw).map((q, i) => ({ ...q, id: i, options: shuffle(q.options) }));

const QuizPreviewModal = ({ title, description, passMark, questions: rawQuestions, onClose }) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState(() => shufflePaper(rawQuestions));
  const [phase, setPhase] = useState('intro'); // intro | sitting | review | done
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [focusLost, setFocusLost] = useState(0);

  const sitting = phase === 'sitting' || phase === 'review';
  const awayRef = useFocusLoss(sitting, { onAway: () => setFocusLost((n) => n + 1) });
  useKeyGuard(sitting);

  const quiz = useMemo(() => ({
    title,
    description,
    passMark,
    questionCount: rawQuestions.length,
    maxScore: rawQuestions.reduce((s, q) => s + q.points, 0),
    mustPassCount: rawQuestions.filter((q) => q.mustPass).length,
  }), [title, description, passMark, rawQuestions]);

  const unanswered = questions.filter((q) => !isAnswered(answers, q)).length;

  const begin = () => {
    setQuestions(shufflePaper(rawQuestions));
    setAnswers({});
    setIndex(0);
    setResult(null);
    setFocusLost(0);
    awayRef.current = false;
    setPhase('sitting');
  };

  // Same grading the server applies: single = the one picked key IS the key;
  // multiple = the picked set equals the key set, all or nothing. A missed
  // critical question fails the sitting whatever the percentage; critical
  // questions alone create a verdict even without a pass mark.
  const submit = () => {
    let score = 0;
    let maxScore = 0;
    let correctCount = 0;
    let criticalMissed = 0;
    questions.forEach((q) => {
      maxScore += q.points;
      const picked = answers[q.id] || [];
      const key = q.options.filter((o) => o.correct).map((o) => o.key);
      const right = q.type === 'single'
        ? picked.length === 1 && key.length === 1 && picked[0] === key[0]
        : picked.length > 0 && sameSet(picked, key);
      if (right) { score += q.points; correctCount += 1; }
      else if (q.mustPass) criticalMissed += 1;
    });
    const percent = maxScore > 0 ? Math.round((score / maxScore) * 10000) / 100 : 0;
    const hasCritical = questions.some((q) => q.mustPass);
    const passed = passMark == null && !hasCritical
      ? null
      : (passMark == null || percent >= passMark) && criticalMissed === 0;
    setResult({
      score, maxScore, percent, passed, correctCount, criticalMissed,
      questionCount: questions.length,
    });
    setConfirmSubmit(false);
    setPhase('done');
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 overflow-y-auto">
      {/* Preview bar — the only thing on screen a participant does not get */}
      <div className="sticky top-0 z-10 bg-slate-800 text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
          <p className="flex-1 min-w-0 text-sm font-semibold truncate">Preview</p>
          {sitting && (
            <span
              className="shrink-0 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs font-medium tabular-nums"
              title="Counted the same way in a real sitting and shown to L&D next to the score. Participants do not see this count."
            >
              Focus lost ×{focusLost}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-sm text-slate-200 hover:text-white hover:bg-slate-700 rounded"
          >
            <X className="w-4 h-4" /> Close Preview
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 relative">
          <div className="mb-4">
            <h1 className="text-lg font-bold text-slate-900">Post-Training Quiz</h1>
            {sitting && <p className="text-xs text-slate-500 truncate">{title}</p>}
          </div>

          {/* Intro */}
          {phase === 'intro' && (
            <div className="space-y-5">
              <QuizFacts quiz={quiz} />
              <SittingRules passMark={passMark} mustPassCount={quiz.mustPassCount} />
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500">Ready when you are.</p>
                <button
                  type="button"
                  onClick={begin}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  Start Quiz
                </button>
              </div>
            </div>
          )}

          {phase === 'sitting' && questions[index] && (
            <SecurePaper watermark={user?.email || ''}>
              <QuestionStep
                questions={questions}
                index={index}
                answers={answers}
                onPick={(q, key) => setAnswers((prev) => pickAnswer(prev, q, key))}
                onIndex={setIndex}
                onReview={() => setPhase('review')}
              />
            </SecurePaper>
          )}

          {phase === 'review' && (
            <SecurePaper watermark={user?.email || ''}>
              <ReviewStep
                questions={questions}
                answers={answers}
                onOpen={(i) => { setIndex(i); setPhase('sitting'); }}
                onSubmit={() => setConfirmSubmit(true)}
              />
            </SecurePaper>
          )}

          {/* Result — the participant's result card, plus the focus count
              only the preview shows. */}
          {phase === 'done' && result && (
            <div className="space-y-4">
              <QuizFacts quiz={quiz} />
              <QuizResultCard result={result} />
              <p className="text-xs text-slate-500">
                Focus lost {focusLost} time{focusLost === 1 ? '' : 's'} during this preview — in a real sitting L&amp;D sees this count, the participant does not.
              </p>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={begin}
                  className="px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Sit It Again
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  Close Preview
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmSubmit && (
        <ConfirmSubmitModal
          unanswered={unanswered}
          onCancel={() => setConfirmSubmit(false)}
          onConfirm={submit}
        />
      )}
    </div>
  );
};

export default QuizPreviewModal;
